import type {
  AnswerOutcome,
  AnswerView,
  CompareRow,
  FeedbackView,
  FieldState,
  LastAnswer,
  Presence,
} from './api';

// «Confronto con Dokless»: pure helpers of the page and the send button (unit-tested).

export const SHOW = ['all', 'diff', 'one_side'] as const;
export type Show = (typeof SHOW)[number];

/** The tone of a field's state: equal green, different red (amber when the trial expects it), one side info. */
export function stateTone(state: FieldState, expected: boolean): 'ok' | 'bl' | 'wa' | 'in' {
  if (state === 'equal') return 'ok';
  if (expected) return 'wa';
  return state === 'different' ? 'bl' : 'in';
}

export function presenceTone(presence: Presence): 'ok' | 'in' | 'wa' {
  return presence === 'both' ? 'ok' : presence === 'unknown' ? 'wa' : 'in';
}

/** The rows the filter shows; `shipment` keeps one shipment (S06's link). */
export function visibleRows(items: readonly CompareRow[], show: Show, shipment?: string): CompareRow[] {
  return items.filter((row) => {
    if (shipment && row.shipment_id !== shipment) return false;
    if (show === 'diff') return row.unexpected > 0;
    if (show === 'one_side') return row.presence === 'only_plancia' || row.presence === 'only_dokless';
    return true;
  });
}

export type SendGate =
  | { enabled: true }
  | { enabled: false; reason: 'owner_only' | 'not_on_dokless' | 'dokless_unknown' | 'no_shipment' };

/**
 * Whether «Manda a Dokless» opens (ADR 0070): owner only, and the shipment in
 * Dokless's latest list (the 5-minute snapshot). The preview asks Dokless
 * again at click time; the API checks everything again before sending.
 */
export function sendGate(
  role: string | undefined,
  row: Pick<CompareRow, 'presence' | 'shipment_id'> | null,
): SendGate {
  if (role !== 'owner') return { enabled: false, reason: 'owner_only' };
  if (!row?.shipment_id) return { enabled: false, reason: 'no_shipment' };
  if (row.presence === 'unknown') return { enabled: false, reason: 'dokless_unknown' };
  if (row.presence !== 'both') return { enabled: false, reason: 'not_on_dokless' };
  return { enabled: true };
}

/** The exact sentence or nothing: no trimming, case kept (like the write switch). */
export const sentenceOk = (typed: string, sentence: string | null) => sentence !== null && typed === sentence;

/** «0,12 $» / «$0.12». */
export function usd(value: number, lang: string): string {
  return new Intl.NumberFormat(lang === 'en' ? 'en-US' : 'it-IT', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Read outcomes, most frequent first: «ok 12 · not_found 2». */
export function outcomesLine(outcomes: Record<string, number>): string {
  return Object.entries(outcomes)
    .sort(([a, x], [b, y]) => y - x || a.localeCompare(b))
    .map(([k, n]) => `${k} ${n}`)
    .join(' · ');
}

/**
 * The new-road alert Dokless has not seen (ADR 0077, 0078): a passage block first
 * («blocco su Suez, Dokless non aggiornato»), else the vessel's own change
 * («rotta cambiata, Dokless non aggiornato»).
 */
export function routeNoticeOf(row: CompareRow): { code: string; passage: string | null } | null {
  const notices = row.plancia?.route_notices ?? [];
  return (
    notices.find((n) => n.code === 'passage_closed' && n.passage) ??
    notices.find((n) => n.code === 'route_changed') ??
    notices.find((n) => n.code === 'passage_reopened' && n.passage) ??
    null
  );
}

/**
 * The passages whose change Dokless has not received yet (ADR 0078): the
 * page's top banner, one line per passage and alert, with how many shipments
 * carry it in a prepared, unsent `full`.
 */
export function passageBanner(
  rows: readonly CompareRow[],
): Array<{ code: string; passage: string; shipments: number }> {
  const out = new Map<string, { code: string; passage: string; shipments: number }>();
  for (const row of rows)
    for (const notice of row.plancia?.route_notices ?? []) {
      if (!notice.code.startsWith('passage_') || !notice.passage || notice.code === 'passage_restricted')
        continue;
      const key = `${notice.code}|${notice.passage}`;
      const entry = out.get(key) ?? { code: notice.code, passage: notice.passage, shipments: 0 };
      entry.shipments += 1;
      out.set(key, entry);
    }
  return [...out.values()].sort((a, b) => b.shipments - a.shipments || a.passage.localeCompare(b.passage));
}

// ── Dokless's answers and feedback (rules v1 §9, §10; ADR 0084) ──────────────

/** The pill's tone of an answer: all green, partial amber, none red, a replay or today's 201 neutral. */
export function answerTone(outcome: AnswerOutcome): 'ok' | 'wa' | 'bl' | 'in' | 'pl' {
  switch (outcome) {
    case 'all':
      return 'ok';
    case 'partial':
      return 'wa';
    case 'none':
      return 'bl';
    case 'stale_revision':
      return 'in';
    default:
      return 'pl';
  }
}

/** An answer worth a pill on a row: Dokless discarded something or ignored a replay. */
export const answerWorthShowing = (answer: LastAnswer | null | undefined): answer is LastAnswer =>
  !!answer &&
  (answer.outcome === 'partial' ||
    answer.outcome === 'none' ||
    answer.outcome === 'stale_revision' ||
    answer.rejected_count > 0);

/** «30 %» / «30 %» of a share in [0, 1]. */
export const percent = (share: number) => `${Math.round(share * 100)} %`;

/** Dokless's texts in the reader's language, the other one when that is empty. */
export function localized(message: { it: string; en: string } | null | undefined, lang: string): string {
  if (!message) return '';
  const first = lang.startsWith('en') ? message.en : message.it;
  return first || (lang.startsWith('en') ? message.it : message.en);
}

/** Feedback nobody at Dokless decided yet (`resolution: null`). */
export const openFeedback = (items: readonly FeedbackView[]) => items.filter((f) => f.resolution === null);

/** «route_off_course 3 · enum_unknown 1», most frequent first. */
export function rulesLine(byRule: Record<string, number>): string {
  return Object.entries(byRule)
    .sort(([a, x], [b, y]) => y - x || a.localeCompare(b))
    .map(([rule, n]) => `${rule} ${n}`)
    .join(' · ');
}

/** The answer Dokless gave to a payload, by payload id (S06's rows). */
export function answersByPayload(answers: readonly AnswerView[]): Map<string, AnswerView> {
  return new Map(answers.map((a) => [a.payload_id, a]));
}
